import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth, roleHome } from "./context/AuthContext";
import MarketingLayout from "./components/marketing/MarketingLayout";
import DashboardLayout from "./components/dashboard/DashboardLayout";
import Landing from "./pages/marketing/Landing";
import { About, FeaturesPage, Pricing, Contact, Legal, RefundPolicy } from "./pages/marketing/Pages";
import Login from "./pages/auth/Login";
// Legacy password-based Register is now hidden — /register redirects to the new passwordless chooser.
import AuthCallback from "./pages/auth/AuthCallback";
import Onboarding from "./pages/auth/Onboarding";
import ForgotPassword from "./pages/auth/ForgotPassword";
import { ResetPassword, VerifyEmail } from "./pages/auth/AuthExtras";
import InvestorDashboard from "./pages/investor/InvestorDashboard";
import Trades from "./pages/investor/Trades";
import MT5Connection from "./pages/investor/MT5Connection";
import Reports from "./pages/investor/Reports";
import Notifications from "./pages/investor/Notifications";
import Withdraw from "./pages/investor/Withdraw";
import Billing from "./pages/investor/Billing";
import Positions from "./pages/investor/Positions";
import Profile from "./pages/investor/Profile";
import ManagerDashboard from "./pages/manager/ManagerDashboard";
import Clients from "./pages/manager/Clients";
import ManagerClients from "./pages/manager/ManagerClients";
import ManagerLivePositions from "./pages/manager/ManagerLivePositions";
import ManagerClientDetail from "./pages/manager/ClientDetail";
import TradeIntelligence from "./pages/manager/TradeIntelligence";
import AllTrades from "./pages/manager/AllTrades";
import Signals from "./pages/manager/Signals";
import SignalsHub from "./pages/manager/SignalsHub";
import Performance from "./pages/manager/Performance";
import AdminDashboard from "./pages/admin/AdminDashboard";
import Users from "./pages/admin/Users";
import ClientDetail from "./pages/admin/ClientDetail";
import Alerts from "./pages/admin/Alerts";
import MT5Approvals from "./pages/admin/MT5Approvals";
import Payments from "./pages/admin/Payments";
import Debug from "./pages/admin/Debug";
import Executions from "./pages/admin/Executions";
import Status from "./pages/admin/Status";
import Subscriptions from "./pages/admin/Subscriptions";
import EmailCenter from "./pages/admin/EmailCenter";
import GoogleSheets from "./pages/admin/GoogleSheets";
import { Plans, Brokers, AuditLogs, Tickets, Settings } from "./pages/admin/AdminPages";

const FullLoader = () => (
  <div className="min-h-screen bg-[#050505] flex items-center justify-center">
    <div className="text-gray-500 text-xs tracking-[0.3em] uppercase animate-pulse">ChartzFx</div>
  </div>
);

const Protected = ({ roles, children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  // Onboarding gate — super_admin exempt so admin can operate the platform without onboarding
  if (!user.onboarding_completed && user.role !== "super_admin" && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }
  if (roles && !roles.includes(user.role)) return <Navigate to={roleHome(user.role)} replace />;
  return children;
};

const OnboardingRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.onboarding_completed || user.role === "super_admin") return <Navigate to={roleHome(user.role)} replace />;
  return <Onboarding />;
};

const RoleRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.onboarding_completed && user.role !== "super_admin") return <Navigate to="/onboarding" replace />;
  return <Navigate to={roleHome(user.role)} replace />;
};

function AppRouter() {
  const location = useLocation();
  // Synchronous check during render: process OAuth session_id BEFORE any route runs
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route element={<MarketingLayout />}>
        <Route path="/" element={<Landing />} />
        <Route path="/about" element={<About />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<Legal type="privacy" />} />
        <Route path="/terms" element={<Legal type="terms" />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
      </Route>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/onboarding" element={<OnboardingRoute />} />
      <Route path="/dashboard" element={<RoleRedirect />} />

      <Route path="/investor" element={<Protected roles={["investor", "manager", "super_admin"]}><DashboardLayout /></Protected>}>
        <Route index element={<InvestorDashboard />} />
        <Route path="positions" element={<Positions />} />
        <Route path="trades" element={<Trades />} />
        <Route path="mt5" element={<MT5Connection />} />
        <Route path="billing" element={<Billing />} />
        <Route path="reports" element={<Reports />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="withdraw" element={<Withdraw />} />
        <Route path="profile" element={<Profile />} />
      </Route>

      <Route path="/manager" element={<Protected roles={["manager", "super_admin"]}><DashboardLayout /></Protected>}>
        <Route index element={<ManagerDashboard />} />
        <Route path="active" element={<ManagerClients segment="active" />} />
        <Route path="inactive" element={<ManagerClients segment="inactive" />} />
        <Route path="all-clients" element={<ManagerClients segment="all" />} />
        <Route path="live-positions" element={<ManagerLivePositions />} />
        <Route path="client/:accountId" element={<ManagerClientDetail />} />
        <Route path="intelligence" element={<TradeIntelligence />} />
        <Route path="all-trades" element={<AllTrades />} />
        <Route path="signals" element={<SignalsHub />} />
        <Route path="clients" element={<Clients />} />
        <Route path="performance" element={<Performance />} />
      </Route>

      <Route path="/admin" element={<Protected roles={["super_admin"]}><DashboardLayout /></Protected>}>
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<Users />} />
        <Route path="users/:userId" element={<ClientDetail />} />
        <Route path="subscriptions" element={<Subscriptions />} />
        <Route path="alerts" element={<Alerts />} />
        <Route path="mt5-approvals" element={<MT5Approvals />} />
        <Route path="payments" element={<Payments />} />
        <Route path="plans" element={<Plans />} />
        <Route path="brokers" element={<Brokers />} />
        <Route path="audit-logs" element={<AuditLogs />} />
        <Route path="tickets" element={<Tickets />} />
        <Route path="executions" element={<Executions />} />
        <Route path="debug" element={<Debug />} />
        <Route path="status" element={<Status />} />
        <Route path="email" element={<EmailCenter />} />
        <Route path="google-sheets" element={<GoogleSheets />} />
        <Route path="settings" element={<Settings />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" theme="dark" toastOptions={{ style: { background: "#121212", border: "1px solid rgba(255,255,255,0.12)", color: "#F3F4F6" } }} />
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
